import type {
  Category,
  MenuCollection,
  MenuData,
  MenuSection,
} from '@/app/dashboard/templates/components/types/template';

function uniqueById<T extends { id?: string }>(entries: T[]): T[] {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = String(entry?.id || '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function filterSectionsByCategoryIds(
  sections: MenuSection[] | undefined,
  allowedCategoryIds: Set<string>,
): MenuSection[] {
  if (!Array.isArray(sections)) return [];

  return sections
    .map((section) => {
      const categories = (section.categories || []).filter((category) =>
        allowedCategoryIds.has(String(category.id || '')),
      );
      const featuredItems = (section.featuredItems || []).filter((item) =>
        allowedCategoryIds.has(String(item.category_id || '')),
      );

      return {
        ...section,
        categories,
        featuredItems,
      };
    })
    .filter((section) => section.categories.length > 0 || section.featuredItems.length > 0);
}

function filterCategoriesBySection(
  source: Record<string, Category[]> | undefined,
  allowedCategoryIds: Set<string>,
): Record<string, Category[]> {
  if (!source) return {};

  return Object.entries(source).reduce<Record<string, Category[]>>((acc, [key, categories]) => {
    const filtered = (categories || []).filter((category) => allowedCategoryIds.has(String(category.id || '')));
    if (filtered.length > 0) {
      acc[key] = filtered;
    }
    return acc;
  }, {});
}

export function getMenuCollections(menuData: MenuData | undefined | null): MenuCollection[] {
  return Array.isArray(menuData?.menu?.collections) ? menuData!.menu.collections : [];
}

export function getNavigationCategories(
  menuData: MenuData | undefined | null,
  activeCollectionId?: string | null,
): Category[] {
  if (!menuData) return [];
  const collections = getMenuCollections(menuData);

  if (activeCollectionId) {
    const selectedCollection = collections.find((collection) => collection.id === activeCollectionId);
    if (selectedCollection) return selectedCollection.categories || [];
  }

  if (collections.length > 0) {
    return uniqueById(collections.flatMap((collection) => collection.categories || []));
  }

  return menuData.menu.categories || [];
}

export function filterMenuDataForNavigation(
  menuData: MenuData,
  activeCollectionId?: string | null,
  activeCategoryId?: string | null,
): MenuData {
  const allCollections = getMenuCollections(menuData);
  const collectionScoped = activeCollectionId
    ? allCollections.filter((collection) => collection.id === activeCollectionId)
    : allCollections;

  const collectionScopedCategories = collectionScoped.length > 0
    ? uniqueById(collectionScoped.flatMap((collection) => collection.categories || []))
    : menuData.menu.categories || [];

  const categories = activeCategoryId
    ? collectionScopedCategories.filter((category) => category.id === activeCategoryId)
    : collectionScopedCategories;

  const allowedCategoryIds = new Set(categories.map((category) => String(category.id || '')));
  const items = (menuData.menu.items || menuData.items || []).filter((item) =>
    allowedCategoryIds.has(String(item.category_id || '')),
  );
  const featuredItems = (menuData.menu.featuredItems || menuData.featuredItems || []).filter((item) =>
    allowedCategoryIds.has(String(item.category_id || '')),
  );

  const filteredCollections = collectionScoped.map((collection) => {
    const collectionCategories = (collection.categories || []).filter((category) =>
      allowedCategoryIds.has(String(category.id || '')),
    );
    const collectionCategoryIds = new Set(collectionCategories.map((category) => String(category.id || '')));

    return {
      ...collection,
      categories: collectionCategories,
      items: (collection.items || []).filter((item) => collectionCategoryIds.has(String(item.category_id || ''))),
      featuredItems: (collection.featuredItems || []).filter((item) =>
        collectionCategoryIds.has(String(item.category_id || '')),
      ),
      sections: filterSectionsByCategoryIds(collection.sections, collectionCategoryIds),
      categoriesBySection: filterCategoriesBySection(collection.categoriesBySection, collectionCategoryIds),
    };
  });

  return {
    ...menuData,
    menu: {
      ...menuData.menu,
      collections: filteredCollections,
      categories,
      items,
      featuredItems,
      sections: filterSectionsByCategoryIds(menuData.menu.sections, allowedCategoryIds),
      categoriesBySection: filterCategoriesBySection(menuData.menu.categoriesBySection, allowedCategoryIds),
    },
    items,
    featuredItems,
  };
}
